import { useMemo } from 'react'
import { isDataRouteEnabled } from '@/features/data/datasets'
import { useCurrentOrganization } from './use-current-organization'
import { extractRouteMetadataByPath } from '@/lib/routing/route-metadata-extractor'
import { collectFeatureRoutes } from '@/lib/routing/routing.collector'
import type {
  AppRoute,
  DynamicNavItemGroup,
  NavItemGroup,
  ProcessedNavItemGroup,
  SyncableItem,
} from '@/lib/routing/routing.types'
import {
  FOOTER_ITEMS,
  NAV_ITEMS,
  PROFILE_DROPDOWN_ITEMS,
} from '@/lib/routing/sidebar-navigation'
import { useDynamicNavigationGroups } from './use-dynamic-navigation'
import { useFeatureFlag } from './use-feature-flag'
import { useOrganizationFeatureFlags } from './use-organization-feature-flags'
import { useRbacNavigation, useRbacNavigationGroups } from './use-rbac-navigation'

/**
 * Synchronizes navigation items with route metadata.
 *
 * For each navigation item, extracts the corresponding route metadata and applies it
 * if the item doesn't have its own metadata (sidebar override capability).
 * Works with both NavItem[] and DynamicNavItem[] arrays.
 *
 * @param items - Array of navigation items to synchronize
 * @param featureRoutes - Array of feature routes to extract metadata from
 * @returns Array of navigation items with synchronized metadata
 */
function synchronizeItemsWithRoutes<T extends SyncableItem>(
  items: T[],
  featureRoutes: AppRoute[],
): T[] {
  return items.map((item) => {
    // Only extract route metadata if the item has a 'to' property
    let routeMeta: ReturnType<typeof extractRouteMetadataByPath> | undefined
    if (item.to) {
      const routePath = typeof item.to === 'string' ? item.to : item.to.pathname || ''
      routeMeta = extractRouteMetadataByPath(featureRoutes, routePath)
    }

    // Sidebar override: if sidebar has permission metadata, use it; otherwise use route metadata
    const meta = item.meta || routeMeta

    return {
      ...item,
      meta,
    }
  })
}

/**
 * Hook that provides sidebar navigation items with permissions synchronized
 * with the actual route definitions, with sidebar override capability.
 *
 * This hook automatically extracts metadata from the route tree and applies
 * it to sidebar navigation items. The sidebar can override route permissions
 * by defining its own metadata, allowing for independent UI visibility control.
 *
 * Supports both static and dynamic navigation groups:
 * - Static groups: Defined with navItems array
 * - Dynamic groups: Defined with dynamicKey and dataFetcher
 *
 * Priority order:
 * 1. Sidebar metadata (if defined) - takes precedence
 * 2. Route metadata (fallback) - used when sidebar has no metadata
 *
 * This enables scenarios like:
 * - Hide sidebar link but allow direct URL access (UI-only restriction)
 * - Show sidebar link but block access at route level (UI visibility without access)
 *
 * @returns Object containing filtered navigation items with synchronized permissions
 */
export function useSyncedSidebarNavigation() {
  const featureRoutes = useMemo(() => collectFeatureRoutes(), [])
  const isKeywordResearchEnabled = useFeatureFlag('keyword-research-ui')
  const { isDataModuleEnabled } = useOrganizationFeatureFlags()
  const { organization } = useCurrentOrganization()
  const dataSections = organization?.aiVisibility?.sections

  // Get dynamic groups
  const dynamicGroups = useMemo(() => {
    return NAV_ITEMS.filter(
      (group): group is DynamicNavItemGroup => 'dynamicKey' in group,
    )
  }, [])

  // Process dynamic groups
  const processedDynamicGroups = useDynamicNavigationGroups(dynamicGroups)

  // Combine all groups maintaining original order
  const finalNavGroups = useMemo((): (NavItemGroup | ProcessedNavItemGroup)[] => {
    const processedGroupsMap = new Map(
      processedDynamicGroups.map((group) => [group.dynamicKey, group]),
    )

    const visibleGroups = NAV_ITEMS.filter((group) => {
      if (group.labelKey === 'sidebarNav.data') {
        return isDataModuleEnabled
      }

      return true
    })

    return visibleGroups.map((group) => {
      if ('dynamicKey' in group) {
        return processedGroupsMap.get(group.dynamicKey) || group
      }
      if ('navItems' in group) {
        const synced = synchronizeItemsWithRoutes(group.navItems, featureRoutes)
        const filtered = synced.filter((item) => {
          if (item.to === '/keyword-research') return isKeywordResearchEnabled
          if (typeof item.to === 'string' && item.to.startsWith('/data/')) {
            return isDataRouteEnabled({ sections: dataSections ?? [] }, item.to)
          }
          return true
        })
        return {
          ...group,
          navItems: filtered,
        }
      }
      return group
    })
  }, [
    processedDynamicGroups,
    featureRoutes,
    isKeywordResearchEnabled,
    isDataModuleEnabled,
    dataSections,
  ])

  // Create synchronized profile and footer items
  const syncedProfileItems = useMemo(
    () => synchronizeItemsWithRoutes(PROFILE_DROPDOWN_ITEMS, featureRoutes),
    [featureRoutes],
  )

  const syncedFooterItems = useMemo(
    () => synchronizeItemsWithRoutes(FOOTER_ITEMS, featureRoutes),
    [featureRoutes],
  )

  // Filter items based on RBAC permissions
  const filteredNavItems = useRbacNavigationGroups(finalNavGroups)
  const filteredFooterItems = useRbacNavigation(syncedFooterItems)
  const filteredProfileItems = useRbacNavigation(syncedProfileItems)

  return {
    navItems: filteredNavItems,
    footerItems: filteredFooterItems,
    profileItems: filteredProfileItems,
  }
}
